import api from '../api';
import { fontCacheKey } from './textLegend';

function getStyleMap(el) {
  const styleValue = el.getAttribute('style') || ''; // get the inline style string of the current element
  const map = {}; // object to hold all style properties and values

  styleValue
    .split(';') // split the style string into individual declarations
    .map((part) => part.trim()) // remove whitespace around each declaration
    .filter(Boolean) // remove empty declarations
    .forEach((part) => {
      const colonIndex = part.indexOf(':'); // find the separator between style property and style value
      if (colonIndex === -1) return; // skip invalid style declarations

      const prop = part.slice(0, colonIndex).trim(); // get the style property name
      const value = part.slice(colonIndex + 1).trim(); // get the style property value

      map[prop] = value; // store the style property and value in the map
    });

  return map; // return the parsed style object
} // this helper function takes the inline style string of an element (e.g. style="color: rgba(0, 0, 0, 0.54);") and returns the styles as a map

function splitAlphaColor(value) {
  const v = String(value).trim();

  const rgba = v.match(
    /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)$/i
  ); // check if the color value has the rgba() format

  if (rgba) {
    // split the alpha from the rgba() color and return them seperate:
    const r = Math.max(0, Math.min(255, Math.round(Number(rgba[1]))));
    const g = Math.max(0, Math.min(255, Math.round(Number(rgba[2]))));
    const b = Math.max(0, Math.min(255, Math.round(Number(rgba[3]))));
    const a = rgba[4] == null ? 1 : Math.max(0, Math.min(1, Number(rgba[4])));

    return {
      color:
        '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join(''),
      alpha: a,
    };
  }

  const hex8 = v.match(/^#([0-9a-f]{8})$/i); // check if the color value has the hex8 format

  if (hex8) {
    // split the alpha from the hex8 color and return them seperate:
    const hex = hex8[1];

    return {
      color: `#${hex.slice(0, 6)}`,
      alpha: parseInt(hex.slice(6, 8), 16) / 255,
    };
  }

  return null;
} // this helper function splits the alpha (opacity) value from a color like rgba(...) or #RRGGBBAA and returns them seperate. This is needed so that the alpha (opacity) of texts and icons can be interpreted correctly by Inkscape.

export function normalizeAlphaPaintsForSvgExport(svgRoot) {
  const paintAttrs = [
    { paintAttr: 'fill', opacityAttr: 'fill-opacity' },
    { paintAttr: 'stroke', opacityAttr: 'stroke-opacity' },
    { paintAttr: 'stop-color', opacityAttr: 'stop-opacity' },
    { paintAttr: 'flood-color', opacityAttr: 'flood-opacity' },
  ]; // we will normalize the alpha (opacity) for all fill, stroke, stop-color and flood-color values inside the SVG

  function setStyleMap(el, styleMap) {
    const styleString = Object.entries(styleMap)
      .map(([prop, value]) => `${prop}: ${value}`) // rebuild each style declaration
      .join('; '); // join all style declarations back into one style string

    if (styleString) {
      el.setAttribute('style', styleString); // set the cleaned inline style back on the element
    } else {
      el.removeAttribute('style'); // remove the style attribute if no styles are left
    }
  } // this helper function takes the style map of an element (created by the getStyleMap function), rebuilds a cleaned inline style string from it and sets it back on the element

  function multiplyOpacity(existingValue, alpha) {
    const existing = parseFloat(existingValue || '1'); // get the existing opacity value or default to 1

    return String((Number.isFinite(existing) ? existing : 1) * alpha); // multiply existing opacity with the alpha from the color
  } // this helper function returns the desired opacity for an element (existing opacity * alpha of the color chosen by the user)

  function normalizePaintOnElement(el, paintAttr, opacityAttr, paintValue) {
    if (isPaintKeyword(paintValue, 'none')) return false; // skip 'none'

    const split = splitAlphaColor(paintValue); // get the color without alpha and the alpha value
    if (!split) return false; // skip colors that do not contain an alpha value

    el.setAttribute(paintAttr, split.color); // set the paint color without embedded alpha

    el.setAttribute(
      opacityAttr,
      multiplyOpacity(el.getAttribute(opacityAttr), split.alpha)
    ); // set the alpha separately as the matching opacity attribute

    return true; // tell the caller that the paint value was normalized
  } // this helper function normalizes the color for an element. It splits the alpha (opacity) from color values like rgba(...) and #RRGGBBAA, and sets the opacity as a seperate opacity attribute

  function moveInlineOpacityToAttribute(el, styleMap, opacityAttr) {
    const inlineOpacity = styleMap[opacityAttr]; // get fill-opacity/stroke-opacity from inline style

    if (inlineOpacity == null) return; // skip if no inline opacity exists

    el.setAttribute(opacityAttr, inlineOpacity); // move inline opacity to attribute
    delete styleMap[opacityAttr]; // remove inline opacity so attributes are the final source of truth
  } // this helper function moves fill-opacity/stroke-opacity from inline styles to attribute (as the attributes will be the final source of truth for the downloaded SVG)

  function isPaintKeyword(value, keyword) {
    return String(value).trim().toLowerCase() === keyword;
  } // this helper function checks if a paint value is the given keyword ('none' or 'currentcolor'), whatever its case: CSS keywords are case-insensitive, so the browser shows an uploaded SVG's 'currentcolor' in the picked color, and the export has to resolve it too

  function walk(el, inheritedColor) {
    const styleMap = getStyleMap(el); // get the inline styles of the current element as a map
    let currentColor = inheritedColor; // keep the inherited color value for child elements that use "currentColor"
    const ownColor = styleMap.color || el.getAttribute('color'); // get color from inline style or color attribute (the browser prioritises inline styles)

    // 1) Remember the color from inline styles or the color attribute, if the current element has one
    if (ownColor) {
      currentColor = ownColor; // update inherited color even if it has no alpha, because it overrides parent color
    }

    // 2) Split the alpha from the currentColor
    const splitCurrentColor = splitAlphaColor(currentColor); // only returns a value if the effective color has alpha

    // 3) Remove color from inline styles and attributes after remembering it. This is safe because all effective currentColor usages are flattened to fill/stroke below.
    if (ownColor) {
      delete styleMap.color; // remove color from inline styles so the downloaded SVG relies on explicit fill/stroke
      el.removeAttribute('color'); // remove color attribute so the downloaded SVG relies on explicit fill/stroke
    }

    // 4) Check all paint-like properties on the current element
    for (const { paintAttr, opacityAttr } of paintAttrs) {
      const stylePaintValue = styleMap[paintAttr]; // get the paint value from inline style
      const attrPaintValue = el.getAttribute(paintAttr); // get the paint value from SVG attribute
      const effectivePaintValue = stylePaintValue || attrPaintValue; // inline style has priority over SVG attribute

      // 4.a) Handle fill/stroke from inline styles or attributes.
      if (stylePaintValue) {
        // handle inline style first, because it has priority over SVG attributes
        // if an inline style exists, it has priority over the SVG attribute:
        moveInlineOpacityToAttribute(el, styleMap, opacityAttr); // move the fill/stroke opacity from the inline styles to attribute, so attributes become the final source of truth

        const split = splitAlphaColor(stylePaintValue); // split alpha from inline style fill/stroke if present

        if (split) {
          // if the inline fill/stroke has alpha:
          el.setAttribute(paintAttr, split.color); // move the color from style to an SVG attribute without alpha

          el.setAttribute(
            opacityAttr,
            multiplyOpacity(el.getAttribute(opacityAttr), split.alpha)
          ); // move the alpha from style to fill-opacity or stroke-opacity
        } else {
          // if the inline paint has NO alpha:
          el.setAttribute(paintAttr, stylePaintValue); // move the inline paint to an SVG attribute

          if (isPaintKeyword(stylePaintValue, 'none')) {
            // if the paint of inline style is set to 'none':
            el.removeAttribute(opacityAttr); // remove opacity attribute (because it has no visible effect when paint is none)
          }
        }

        delete styleMap[paintAttr]; // remove inline fill/stroke because it has been flattened to an attribute (a currentColor keyword included: step 4.b resolves it from the attribute)
      } else if (
        attrPaintValue &&
        !isPaintKeyword(attrPaintValue, 'currentcolor')
      ) {
        // only normalize the attribute if no inline fill/stroke exists (because inline styles should always be prioritised over attributes, just as the browser does it):
        moveInlineOpacityToAttribute(el, styleMap, opacityAttr); // move the fill/stroke opacity from the inline styles to attribute, so attributes become the final source of truth

        normalizePaintOnElement(el, paintAttr, opacityAttr, attrPaintValue);
      }

      // 4.b) Resolve currentColor to the actual inherited/default color
      if (isPaintKeyword(effectivePaintValue, 'currentcolor')) {
        moveInlineOpacityToAttribute(el, styleMap, opacityAttr); // move the fill/stroke opacity from the inline styles to attribute (and delete it from the styleMap)

        if (splitCurrentColor) {
          // if currentColor contains alpha:
          el.setAttribute(paintAttr, splitCurrentColor.color); // replace currentColor with the actual color without alpha

          el.setAttribute(
            opacityAttr,
            multiplyOpacity(
              el.getAttribute(opacityAttr),
              splitCurrentColor.alpha
            )
          ); // apply the alpha from currentColor as fill-opacity or stroke-opacity
        } else {
          // if currentColor contains NO alpha:
          el.setAttribute(paintAttr, currentColor); // replace currentColor with the inherited/default color
        }
      }
    }

    setStyleMap(el, styleMap); // write the cleaned inline styles back to the element

    Array.from(el.children).forEach((child) => {
      walk(child, currentColor); // repeat the same process for child elements and pass down the currentColor value
    });
  }
  walk(svgRoot, '#000000'); // start normalizing at the root SVG element, with black as the inherited color, as the browser also uses black as the default for 'currentColor'
} // this helper function goes through all elements of a SVG, splits the alpha (opacity) from color values like rgba(...) and #RRGGBBAA, and sets the opacity as the matching opacity attribute (like fill-opacity, stroke-opacity, stop-opacity, or flood-opacity). It also resolves Iconify/currentColor icons by replacing fill="currentColor" or stroke="currentColor" with the actual color from style="color: ...". This is needed because when opening the downloaded SVG in Inkscape, alpha inside rgba(...) and #RRGGBBAA is not rendered correctly.

export function convertTextNodesToPaths(svgRoot, fonts) {
  const textNodes = svgRoot.querySelectorAll('text'); // find all <text> elements in the svg

  for (const textEl of textNodes) {
    // for all <text> elements:
    const style = getStyleMap(textEl); // get the inline styles of that <text> element
    const fontFamilyRaw = style['font-family'] || ''; // get the font-family from the style
    const cleanedFamily = fontCacheKey(
      fontFamilyRaw.replace(/['"]/g, '')
    ).trim(); // the family as the browser serialises the renderer's font-family: the cache key (utils/textLegend.js), in quotes when the name has a space. Until chain 11 the regex here, meant to strip quotes, brackets and commas, matched nothing (its `[` closed the character class), so a quoted family was never found and its text stayed text in the printing file (fixed on the spot, Nov 2026; no stored font has such a name today)
    if (!cleanedFamily) continue; // skip if no valid font-family

    const fontSize = parseFloat(style['font-size'] || ''); // get font-size as a number
    if (!Number.isFinite(fontSize)) continue; // skip if font-size is missing or invalid

    const font = fonts[cleanedFamily]; // get the fontkit font object from the fonts map
    if (!font) continue; // skip if the font is not found

    // build variation settings (axes) from inline styles:
    const varset = {}; // object to hold variation axis values
    const wght = parseFloat(style['font-weight'] || ''); // extract the numeric font-weight from the style of the current text
    if (Number.isFinite(wght)) varset.wght = wght; // if the value is defined and valid, add it to varset

    const wdth = parseFloat(style['font-stretch'] || ''); // extract the numeric font-stretch from the style of the current text
    if (Number.isFinite(wdth)) varset.wdth = wdth; // if the value is defined and valid, add it to varset

    const slMatch = (style['font-style'] || '').match(
      /oblique\s*([-+]?\d*\.?\d+)deg/
    ); // extract the font-style and find the number inside the font-style string like 'oblique 14deg'
    const slnt = slMatch ? parseFloat(slMatch[1]) : NaN; // convert the string to a float
    if (Number.isFinite(slnt)) varset.slnt = slnt; // if the value is defined and valid, add it to varset

    let vfont = font;
    if (Object.keys(varset).length) {
      try {
        vfont = font.getVariation(varset); // if there were any variation settings found in the style of the current text, apply them to the font
      } catch {
        vfont = font; // fallback to original font if variation fails
      }
    }

    const text = textEl.textContent || ''; // get the actual text from the <text> element
    const x = parseFloat(textEl.getAttribute('x') || '0'); // get the 'x' attribute of the current text (or default to 0)
    const y = parseFloat(textEl.getAttribute('y') || '0'); // get the 'y' attribute of the current text (or default to 0)
    const scale = fontSize / vfont.unitsPerEm; // Calculate scale factor to match font size

    const run = vfont.layout(text); // .layout() generates glyphs and positioning data for the text content
    let d = ''; // path data string
    let cx = x; // current x position
    let cy = y; // current y position

    for (let i = 0; i < run.glyphs.length; i++) {
      // for every glyph of the current text:
      const glyph = run.glyphs[i]; // get the current glyph
      const pos = run.positions[i] || {}; // get the glyph's positioning information

      let gp = glyph.path.scale(scale, -scale); // scale the glyph and flip it vertically to align with the SVG coordinate system

      const gx = cx + (pos.xOffset || 0) * scale; // calculate the glyph's X position with offset
      const gy = cy - (pos.yOffset || 0) * scale; // calculate the glyph's Y position with offset

      gp = gp.translate(gx, gy); // move the glyph to the correct position
      d += gp.toSVG(); // append the glyph’s path data to the overall path data string

      const letterSpacing = parseFloat(style['letter-spacing'] || '0') || 0; // get the letter-spacing from the style
      const wordSpacing = parseFloat(style['word-spacing'] || '0') || 0; // get the word-spacing from the style

      cx += (pos.xAdvance || 0) * scale + letterSpacing; // update current X position for next glyph, adding letter-spacing
      cy += (pos.yAdvance || 0) * scale; // update current Y position for next glyph, by adding the advance height (usually 0 for horizontal text)
      if (glyph.codePoints && glyph.codePoints[0] === 32) cx += wordSpacing; // if the current glyph is a space, add word-spacing
    }

    // determine paint properties (fill, stroke, opacity) from inline style or attributes:
    let fill = style.fill || textEl.getAttribute('fill') || 'black';
    let stroke = style.stroke || textEl.getAttribute('stroke') || 'none';
    let strokeWidth =
      style['stroke-width'] || textEl.getAttribute('stroke-width') || '0';

    if (
      !Number.isFinite(parseFloat(strokeWidth)) ||
      parseFloat(strokeWidth) <= 0
    ) {
      // disable stroke if stroke-width is zero or invalid:
      stroke = 'none';
      strokeWidth = '0';
    }

    const pathEl = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'path'
    ); // create a new SVG <path> element
    pathEl.setAttribute('d', d); // set the 'd' attribute with the path data
    pathEl.setAttribute('fill', fill); // set fill color
    pathEl.setAttribute('stroke', stroke); // set stroke color
    pathEl.setAttribute('stroke-width', String(strokeWidth)); // set stroke width

    textEl.parentNode.replaceChild(pathEl, textEl); // replace the original text element with the new path
  }
} // replaces all <text> elements in an svg with equivalent <path> elements while preserving appearance

export function cloneAndPrepSvg(svg, widthMm, heightMm) {
  const clone = svg.cloneNode(true); // clone the SVG so we can manipulate it without affecting the original
  clone.removeAttribute('style'); // remove the style attribute from the cloned SVG (to get rid of the width and height styles - we now use the width and height in mm added to the svg above)

  // set width and height attributes with units in millimeters:
  clone.setAttribute('width', `${widthMm}mm`);
  clone.setAttribute('height', `${heightMm}mm`);

  // remove elements that should not be included in the download:
  clone
    .querySelectorAll('.dont-include-in-download')
    .forEach((el) => el.remove());

  return clone;
} // clones an SVG and sets dimensions, removing unwanted elements

export function svgToString(svgEl, { withDoctype = false } = {}) {
  const serializer = new XMLSerializer(); // create a new XMLSerializer instance
  const body = serializer.serializeToString(svgEl); // convert the cloned SVG DOM to a string
  if (!withDoctype) return body;

  const doctype =
    '<?xml version="1.0" encoding="UTF-8"?>\n' +
    '<!DOCTYPE svg PUBLIC "-//W3C//DTD SVG 1.1//EN" ' +
    '"http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd">\n';
  return doctype + body;
} // serializes SVG to string with optional XML + doctype

const collectPngUserIconAssetIdsFromSvg = (svgElement) => {
  const assetIds = new Set();

  const imageElements = [
    ...svgElement.querySelectorAll(
      'image[data-user-icon-asset-id][data-user-icon-format="png"]' // find every <image> element that has a data-user-icon-asset-id attribute and a data-user-icon-format attribute whose value is exactly "png"
    ),
  ]; // get all uploaded PNG <image> elements inside the SVG

  // collect all assetIds of all user-uploaded PNG icons:
  imageElements.forEach((imageElement) => {
    const assetId = imageElement.getAttribute('data-user-icon-asset-id'); // get the assetId of the current image element

    if (assetId) {
      assetIds.add(String(assetId)); // add the current assetId to assetIds
    }
  });

  return [...assetIds];
}; // collect all uploaded PNG assetIds that are actually present in the SVG clone

const setSvgImageHref = (imageElement, href) => {
  imageElement.setAttribute('href', href);
  imageElement.removeAttribute('xlink:href');
  imageElement.removeAttributeNS('http://www.w3.org/1999/xlink', 'href');
}; // write the image href once and remove legacy xlink:href to avoid duplicating large data URLs

export const inlinePngUserIconDataUrlsForSvgExport = async (svgElement) => {
  const assetIds = collectPngUserIconAssetIdsFromSvg(svgElement); // collect all uploaded PNG assetIds that are actually present in the SVG clone

  if (assetIds.length === 0) {
    return;
  }

  const { data } = await api.post('/api/user-icons/export-assets', {
    assetIds,
  }); // fetch PNG assets as data URLs from the backend

  const dataUrlsById = Object.fromEntries(
    data.map((asset) => [String(asset.assetId), asset.dataUrl])
  ); // create a fast lookup from assetId to data URL

  const imageElements = [
    ...svgElement.querySelectorAll(
      'image[data-user-icon-asset-id][data-user-icon-format="png"]'
    ),
  ]; // get all uploaded PNG <image> elements inside the SVG clone

  const missingAssetIds = [];

  imageElements.forEach((imageElement) => {
    const assetId = imageElement.getAttribute('data-user-icon-asset-id');
    const dataUrl = dataUrlsById[String(assetId)];

    if (!dataUrl) {
      missingAssetIds.push(String(assetId));
      return;
    }

    setSvgImageHref(imageElement, dataUrl); // replace the temporary signed URL with a permanent embedded data URL
  });

  if (missingAssetIds.length > 0) {
    throw Object.assign(
      new Error(
        'error-key: export-helpers-uploaded-png-icon-assets-embed-failed'
      ),
      {
        details: {
          assetIds: [...new Set(missingAssetIds)].join(', '),
        },
      }
    );
  }
}; // embeds uploaded PNG icons as data URLs so the exported SVG is self-contained
